import React from 'react';
import {hasFeatureFlag} from '@/forecast-app/shared/util/FeatureUtil';
import {useIntl} from 'react-intl';
import {BudgetHeaderCard, FinancialNumber} from './ProgramBudgetTotals';
import {DeprecatedText as Text} from '@forecast-it/design-system';
import {NUMBER_TYPE} from '@/components/new-ui/project/project-budget-v3/util/BudgetUtils';
import {BUDGET_TYPE, PROGRAM_BUDGET_TYPE} from '@/constants';

const ProfitSection = ({program}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: hasFinancialCategoriesFeature ? 'common.profit_margin' : 'common.profit'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'project_budget.actual_margin' : 'project_budget.profit_margin_to_date'
				}
				cy={'profitMargin.toDate'}
				tooltip={'ProfitMarginActualToDate'}
				tooltipMaxWidth={1000}
				value={program.projectsFinancialsToDate.recognitionActualProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
			/>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_margin'
						: 'project_budget.profit_margin_at_completion'
				}
				cy={'profitMargin.atCompletion'}
				tooltip={'ProfitMarginAtCompletion'}
				tooltipMaxWidth={1000}
				value={program.projectsFinancialsTotals.recognitionProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};

const ActualRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'project_budget.revenue_to_date',
				})}
			</Text>
			<FinancialNumber
				translationId={hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'common.recognition_to_date'}
				cy={'revenueRecognition.toDate'}
				tooltip={'RevenueRecognizedToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.toDate.variance'}
				tooltip={'RevenueRecognizedToDateCapVariance'}
				tooltipMaxWidth={450}
				value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};

const ProjectedTotalRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.projected_total_revenue'
						: 'project_budget.revenue_at_completion',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_revenue'
						: 'project_budget.recognition_at_completion'
				}
				cy={'revenueRecognition.atCompletion'}
				tooltip={'RevenueRecognitionAtCompletion'}
				tooltipMaxWidth={600}
				value={program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.atCompletion.variance'}
				tooltip={'RevenueRecognitionAtCompletionCapVariance'}
				tooltipMaxWidth={450}
				value={program.budgetValue - program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};
const RevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');
	const isFixedPrice = program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE;

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'project_budget.revenue'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'common.revenue_recognition_to_date'
				}
				cy={'revenueRecognition.toDate'}
				tooltip={'RevenueRecognizedToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			{isFixedPrice ? (
				<FinancialNumber
					translationId={
						hasFinancialCategoriesFeature ? 'common.remaining_budget' : 'project_budget.remaining_revenue'
					}
					cy={'revenueRecognition.remaining'}
					tooltip={'RemainingRevenue'}
					tooltipMaxWidth={350}
					value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
				/>
			) : (
				<FinancialNumber
					translationId={
						hasFinancialCategoriesFeature
							? 'common.projected_total_revenue'
							: 'common.revenue_recognition_at_completion'
					}
					cy={'revenueRecognition.atCompletion'}
					tooltip={'RevenueRecognitionAtCompletion'}
					tooltipMaxWidth={350}
					value={program.projectsFinancialsTotals.recognitionForecastRevenue}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ActualValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.actual_value_of_service'
						: 'project_budget.value_of_service_to_date',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'common.actual_value_of_service' : 'project_budget.value_of_service_to_date'
				}
				cy={'valueOfService.toDate'}
				tooltip={'SpendActualToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			<FinancialNumber
				translationId={
					program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE
						? 'program_budget.variance_with_fixed_price'
						: 'program_budget.variance_with_cap'
				}
				cy={'valueOfService.toDate.variance'}
				tooltip={
					program.budgetType === BUDGET_TYPE.FIXED_PRICE
						? 'SpendActualToDateFixedPriceVariance'
						: 'SpendActualToDateCapVariance'
				}
				tooltipMaxWidth={450}
				value={program.budgetValue - program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};

const ProjectedTotalValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion'
				}
				cy={'valueOfService.atCompletion'}
				tooltip={'SpendTotalAtCompletion'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			<FinancialNumber
				translationId={
					program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE
						? 'program_budget.variance_with_fixed_price'
						: 'program_budget.variance_with_cap'
				}
				cy={'valueOfService.atCompletion.variance'}
				tooltip={
					program.budgetType === BUDGET_TYPE.FIXED_PRICE
						? 'SpendTotalAtCompletionFixedPriceVariance'
						: 'SpendTotalAtCompletionCapVariance'
				}
				tooltipMaxWidth={350}
				value={program.budgetValue - program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};

const ValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'common.value_of_service'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'common.actual_value_of_service' : 'project_budget.value_of_service_to_date'
				}
				cy={'valueOfService.toDate'}
				tooltip={'SpendActualToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion'
				}
				cy={'valueOfService.atCompletion'}
				tooltip={'SpendTotalAtCompletion'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
			/>
		</BudgetHeaderCard>
	);
};

export const ProgramHeaderSections = {
	ProfitSection: ProfitSection,
	ActualRevenueSection: ActualRevenueSection,
	ProjectedTotalRevenueSection: ProjectedTotalRevenueSection,
	RevenueSection: RevenueSection,
	ActualValueOfServiceSection: ActualValueOfServiceSection,
	ProjectedTotalValueOfServiceSection: ProjectedTotalValueOfServiceSection,
	ValueOfServiceSection: ValueOfServiceSection,
};
